import { computed, Injectable, signal } from '@angular/core';

export interface TranslateLanguage {
  code: string;
  label: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleTranslateService {
  private readonly pageLanguage = 'ka';
  private loaded = false;

  readonly languages: TranslateLanguage[] = [
    { code: 'ka', label: 'KA', flag: 'assets/images/flags/ge.svg' },
    { code: 'en', label: 'EN', flag: 'assets/images/flags/gb.svg' },
    { code: 'ru', label: 'RU', flag: 'assets/images/flags/ru.svg' },
  ];

  currentLanguageCode = signal<string>(this.getCookieLanguage());
  currentLanguage = computed(() =>
    this.languages.find(language => language.code === this.currentLanguageCode()) ?? this.languages[0]
  );
  otherLanguages = computed(() =>
    this.languages.filter(language => language.code !== this.currentLanguage().code)
  );

  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    const element = document.createElement('div');
    element.id = 'google_translate_element';
    element.hidden = true;
    document.body.appendChild(element);

    (window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement({
        pageLanguage: this.pageLanguage,
        includedLanguages: this.languages.map(language => language.code).filter(code => code !== this.pageLanguage).join(','),
        autoDisplay: false
      }, 'google_translate_element');
    };

    const script = document.createElement('script');
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }

  setLanguage(code: string): void {
    if (code === this.currentLanguage().code) {
      return;
    }

    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (code === this.pageLanguage || !combo) {
      this.setLanguageCookie(code);
      location.reload();
      return;
    }

    combo.value = code;
    combo.dispatchEvent(new Event('change', { bubbles: true }));
    this.currentLanguageCode.set(code);
  }

  private getCookieLanguage(): string {
    const match = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/;]*\/([^;]*)/);
    return match?.[1] || this.pageLanguage;
  }

  private setLanguageCookie(code: string): void {
    const expired = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = expired;
    const domainParts = location.hostname.split('.');
    for (let i = 0; i < domainParts.length - 1; i++) {
      document.cookie = `${expired}; domain=.${domainParts.slice(i).join('.')}`;
    }

    if (code !== this.pageLanguage) {
      document.cookie = `googtrans=/${this.pageLanguage}/${code}; path=/`;
    }
  }
}
